import { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { useQuery } from "@tanstack/react-query";
import { fetchAllUsers } from '../../api/users';
import { useTranslation } from 'react-i18next';

const EMPTY_FORM = { telegramId: '', date: '', house: '', price: ''};

// Bottom sheet (adding or editing record)
const RecordSheet = ({ isOpen, mode, record, onClose, onSubmit}) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const { t } = useTranslation();
    const { data: users = [] } = useQuery({
        queryKey: ['users'],
        queryFn: fetchAllUsers
    });

    // Populate form when editing
    useEffect(() => {
        if (mode === 'edit' && record) {
            setForm({
                telegramId: '',
                date: record.date, 
                house: record.house,
                price: record.price
            });
        } else {
            setForm(EMPTY_FORM);
        }
    }, [mode, record]);

    const handleChange = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value}));


    const handleSubmit = () => {
            const payload = mode === 'add'
                ? { telegram_id: parseInt(form.telegramId), date: form.date, house: form.house, price: parseFloat(form.price) }
                : {record_id: record.id, date: form.date, house: form.house, price: parseFloat(form.price) };
            onSubmit(payload);
        };

    if (!isOpen) return null;

    return (
            <div className="fixed inset-0 z-50 bg-black/60" onClick={onClose}>
                <div className="absolute bottom-0 left-0 right-0 bg-background-400 border-t border-primary-50 rounded-t-2xl px-5 pt-3 pb-10 overflow-hidden" 
                onClick={e => e.stopPropagation()}>
                    {/* Drag handle */}
                    <div className="w-9 h-1 bg-primary-600 rounded-full mx-auto mb-5" />
                    
                    {/* Sheet header */}
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-white text-base font-semibold">{mode === 'add' ? t('records.add_record_title') : t('records.edit_record_title')}</h3>
                        <button onClick={onClose}>
                            <IoClose className="text-primary-50 text-xl" />
                        </button>
                    </div>

                    {/* Worker selector - only add */}
                    {mode === 'add' && (
                        <div className="mb-4">
                            <label className="block text-xs text-primary-50 font-mono mb-1.5">WORKER</label>
                            <select
                                value={form.telegramId}
                                onChange={handleChange('telegramId')}
                                className="w-full bg-background-600 text-white border border-primary-50 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-secondary-800
                                placeholder:text-primary-10 focus:ring-1 focus:ring-secondary-800">
                                    <option value="">{t('records.worker_select_placeholder')}</option>
                                    {users.map((u,i) => (
                                        <option value={u.telegram_id} key={i}>{u.name}</option>
                                    ))}
                            </select>
                        </div>
                    )}
                    {/* Date */}
                    <div className="mb-4">
                        <label className="block text-xs text-primary-50 font-mono mb-1.5">DATE</label>
                        <input 
                            type='date'
                            value={form.date}
                            onChange={handleChange('date')}
                            className="w-full bg-background-600 text-white border border-primary-50 rounded-xl px-3 py-3 text-sm focus:outline-none
                            placeholder:text-primary-10 focus:border-secondary-800 focus:ring-1 focus:ring-secondary-800"/>
                    </div>
                    {/* House */}
                    <div className="mb-4">
                        <label className="block text-xs text-primary-50 font-mono mb-1.5">HOUSE</label>
                        <input 
                            type='text' 
                            placeholder={t('records.house_input_placeholder')}
                            value={form.house}
                            onChange={handleChange('house')}
                            className="w-full bg-background-600 text-white border border-primary-50 rounded-xl px-3 py-3 text-sm 
                            placeholder:text-primary-10 focus:outline-none focus:border-secondary-800 focus:ring-1 focus:ring-secondary-800"/>
                    </div>
                    {/* Price */}
                    <div className="mb-4">
                        <label className="block text-xs text-primary-50 font-mono mb-1.5">PRICE</label>
                        <input 
                            type='number'
                            inputMode="decimal"
                            placeholder="0.00"
                            value={form.price}
                            onChange={handleChange('price')}
                            className="w-full bg-background-600 text-white border border-primary-50 rounded-xl px-3 py-3 text-sm 
                            placeholder:text-primary-10 focus:outline-none focus:border-secondary-800 focus:ring-1 focus:ring-secondary-800"/>
                    </div>
                    {/* Actions */}
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 bg-primary-800 text-primary-50 rounded-xl py-3 text-xs font-mono">
                            {t('general.cancel')}
                        </button>
                        <button
                            onClick={handleSubmit}
                            className="flex-1 bg-secondary-800 text-white rounded-xl py-3 text-xs font-mono font-bold">
                            {mode === 'add' ? t('records.add_record') : t('records.save_changes')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

export default RecordSheet;